/*
  CO-MAC: endless scrolling on collection pages (snippets/collection-infinite-scroll.liquid).

  - The next page loads automatically as the customer nears the bottom, using the
    numbered pagination's own "next" link through Shopify's section rendering, so
    filters and sort carry over. A running count shows how far through they are,
    and a "Load more" button is always there as a manual way on.
  - "Near the bottom" is watched two ways, an IntersectionObserver on this element
    plus a light scroll/resize check, so it fires even where one of them doesn't.
  - A page that fails to load is not retried automatically (the button offers to
    try again) and the reason is logged to the browser console.
  - Filters and sort (assets/facets.js) replace the whole grid, this element
    included, so a fresh element takes over after every change.
  - Back from a product returns to the same spot: the pages loaded and the scroll
    position are kept for this tab and restored.
*/
(function () {
  if (!window.customElements || customElements.get('collection-infinite-scroll')) return;

  var MEMORY = 'comac:collection-scroll';
  var MAX_RESTORE_PAGES = 12;

  function cameBack() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return !!nav && nav.type === 'back_forward';
    } catch (error) {
      return false;
    }
  }

  function here() {
    return window.location.pathname + window.location.search;
  }

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.grid = document.getElementById('product-grid');
      this.nextUrl = this.dataset.nextUrl || '';
      this.sectionId = this.dataset.sectionId || '';
      this.startPage = Number(this.dataset.page) || 1;
      this.page = this.startPage;
      this.total = Number(this.dataset.total) || 0;
      this.loading = false;
      this.foot = this.querySelector('.collection-infinite__foot');
      this.status = this.querySelector('.collection-infinite__status');
      this.moreButton = this.querySelector('.collection-infinite__more');

      // Without these, the numbered pagination stays as it is.
      if (!this.grid || !this.nextUrl || !this.sectionId || !window.fetch || !window.DOMParser) return;

      this.classList.add('is-enhanced');
      this.foot.hidden = false;
      this.moreButton.hidden = false;
      this.moreButton.addEventListener('click', () => {
        this.failed = false;
        this.moreButton.textContent = 'Load more';
        this.loadNext();
      });
      this.onPageHide = () => this.saveSpot();
      window.addEventListener('pagehide', this.onPageHide);

      // Near the bottom: watched two ways so it always fires.
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.check();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.check();
        }, { rootMargin: '0px 0px 900px 0px' });
        this.observer.observe(this);
      }

      this.updateStatus();
      this.restoreSpot();
      window.requestAnimationFrame(() => this.check());
    }

    disconnectedCallback() {
      this.stopWatching();
      if (this.onPageHide) window.removeEventListener('pagehide', this.onPageHide);
    }

    stopWatching() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }

    // Load the next page if the end of the list is within 900px of the screen.
    check() {
      if (!this.nextUrl || this.loading || this.failed || !this.isConnected) return;
      if (this.getBoundingClientRect().top < window.innerHeight + 900) this.loadNext();
    }

    shown() {
      return Array.prototype.filter.call(this.grid.children, (el) => el.classList.contains('grid__item')).length;
    }

    updateStatus(text) {
      if (text) {
        this.status.textContent = text;
      } else if (!this.nextUrl) {
        this.status.textContent = this.startPage > 1 ? '' : 'Showing all ' + this.total + ' products';
      } else {
        this.status.textContent = 'Showing ' + this.shown() + ' of ' + this.total + ' products';
      }
    }

    // Fetch the next page's grid and add its products to this one. A call made
    // while a page is already loading waits for that page instead.
    loadNext() {
      if (this.loading) return this.pending;
      if (!this.nextUrl) return Promise.resolve(false);
      this.loading = true;
      this.moreButton.hidden = true;
      this.setAttribute('aria-busy', 'true');
      this.updateStatus('Loading more products\u2026');

      var url = this.nextUrl + (this.nextUrl.indexOf('?') < 0 ? '?' : '&') + 'section_id=' + encodeURIComponent(this.sectionId);

      this.pending = fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error('page ' + response.status);
          return response.text();
        })
        .then((html) => {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('#product-grid > .grid__item');
          var next = doc.querySelector('collection-infinite-scroll');
          var added = document.createDocumentFragment();
          items.forEach((item) => added.appendChild(document.importNode(item, true)));
          this.grid.appendChild(added);

          this.page += 1;
          this.nextUrl = (next && next.getAttribute('data-next-url')) || '';
          if (!items.length) this.nextUrl = '';
          this.loading = false;
          this.removeAttribute('aria-busy');
          this.updateStatus();

          if (!this.nextUrl) {
            this.stopWatching();
          } else {
            this.moreButton.hidden = false;
            this.check();
          }
          return true;
        })
        .catch((error) => {
          console.warn('Collection endless scroll: could not load ' + url, error);
          this.loading = false;
          this.failed = true;
          this.removeAttribute('aria-busy');
          this.updateStatus();
          this.moreButton.textContent = 'Couldn\u2019t load more \u2014 try again';
          this.moreButton.hidden = false;
          return false;
        });
      return this.pending;
    }

    saveSpot() {
      try {
        sessionStorage.setItem(MEMORY, JSON.stringify({
          url: here(),
          start: this.startPage,
          page: this.page,
          y: window.scrollY || window.pageYOffset || 0
        }));
      } catch (error) {}
    }

    // Back from a product: load the pages that were showing, then scroll back.
    restoreSpot() {
      if (!cameBack()) return;
      var saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(MEMORY) || 'null');
      } catch (error) {}
      if (!saved || saved.url !== here() || saved.start !== this.startPage) return;

      var target = Math.min(saved.page, this.startPage + MAX_RESTORE_PAGES);
      var step = () => {
        if (this.page >= target || !this.nextUrl) {
          window.scrollTo(0, saved.y || 0);
          return;
        }
        this.loadNext().then((ok) => {
          if (ok) step();
        });
      };
      step();
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
